import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { MaterialReactTable } from "material-react-table";
import { decodeHtml } from "../../utils/decodeHtml";
import { currency_converter_inr } from "../../utils/currencyUtils";
import RevenueTrend from "./components/RevenueTrend";
import NetAddTrend from "./components/NetAddTrend";
import MonthlyContribution from "./components/MonthlyContribution";
import ByIndustry from "./components/ByIndustry";
import ByGeography from "./components/ByGeography";
import TeamRevenueAnalysis from "./components/TeamRevenueAnalysis";
import VOBTAnalysis from "./components/VOBTAnalysis";
import AddMonthlyRevenueTarget from "./components/AddMonthlyRevenueTarget";
import AddMonthlyNetaddTarget from "./components/AddMonthlyNetaddTarget";
import useSmartBack from "../../hooks/useSmartBack";

function RevenueDetails() {
  const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [searchParams] = useSearchParams();
  const [options, setoptions] = useState([]);
  const [previousYearOptions, setPreviousYearOptions] = useState([]);
  const {setGlobalLoading, token} = useOutletContext();
  const [showMonthlyTargetModal, setShowMonthlyTargetModal] = useState(false);
  const [showMonthlyNetaddTargetModal, setShowMonthlyNetaddTargetModal] = useState(false);
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const searchUserId = searchParams.get("user_id");

  const passedUser = searchUserId
  ? {
      id: searchUserId,
      firstname: searchParams.get("firstname"),
      lastname: searchParams.get("lastname"),
    }
  : null;
  const [selectedUser, setSelectedUser] = useState( passedUser || null);
  const passedYear = searchParams.get("year");
  const [userList, setUserList] = useState([]);

  const handleBack = useSmartBack("/order/list");
  const { setValue } = useForm();
  const loggedInUserId = user?.id;
  const isAdmin = user && user?.admin === "1";
  const canAddMonthlyTarget = isAdmin ||  Number(loggedInUserId) === Number(selectedUser?.id);
  const formatRevenueInCr = (value) => {
      const cr = Number(value || 0) / 10000000;
      return `₹${Number(cr.toFixed(2))} Cr`;
    };
  const [data, setData] = useState([]);
  const today = new Date();
  const currentFY =
    today.getMonth() + 1 >= 4
      ? `${today.getFullYear()}-${today.getFullYear() + 1}`
      : `${today.getFullYear() - 1}-${today.getFullYear()}`;

//  const [selectedYear, setSelectedYear] = useState(currentFY);
  const [selectedYear, setSelectedYear] = useState(passedYear || currentFY);

//  const id = user.id;
  const id = selectedUser?.id || user.id;

    useEffect(() => {
      const getBasicDetails = async () => {
        try {
          setGlobalLoading(true);
          const response = await axios.get(
            `${REACT_APP_API_BASE_URL}/performance/user_details`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setUserList(response.data);

           if (response.data?.user?.length > 0) {

            // If user came from Overview page
            if (passedUser) {
              setSelectedUser(passedUser);
              setValue("user_id", passedUser.id);
              return;
            }

            // Find logged in user
            const defaultUser = response.data.user.find(
              (item) => Number(item.id) === Number(user.id)
            );

            // If login user not found → Firm Wide
            const selected = defaultUser || {
              id: "9",
              firstname: "Firm",
              lastname: "Wide",
            };

            setSelectedUser(selected);
            setValue("user_id", selected.id);
          }
        } catch (error) {
          console.log(error);
          setGlobalLoading(false);
        } finally {
          setGlobalLoading(false);
        }
      };
      getBasicDetails();
    }, []);

  const get_revenue_data = async () => {
    setGlobalLoading(true);
    try {
      const response = await axios.get(
        `${REACT_APP_API_BASE_URL}/invoices/get_revenue_data/${id}?year=${selectedYear}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const d = response.data;
      if (response.status === 200) {
        const current_revenue = d?.revenue_current_year?.[0]?.total_revenue || 0;
        const prev_revenue = d?.revenue_prev_year?.[0]?.sum || 1;
        const revenue_growth = (((current_revenue - prev_revenue) / prev_revenue) * 100).toFixed(2);
        const revenueTarget = Number(d?.revenue_target?.[0]?.target || 0);
        const revenueAchievement = revenueTarget > 0 ? (current_revenue / revenueTarget) * 100 : 0;

        setoptions(d.revenue_invoice_list || []);
        setPreviousYearOptions(d.previous_revenue_invoice_list || []);
        setData({
          ...d,
          current_revenue,
          revenue_growth,
          revenueTarget,
          revenueAchievement,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setGlobalLoading(false);
    }
  };

 useEffect(() => {
  get_revenue_data();
}, [id, selectedYear]);

  const financialYears = Array.from({ length: 5 }, (_, i) => {
    const start = new Date().getFullYear() - i;
    const end = start + 1;
    return {
      label: `${start}-${end}`,
      value: `${start}-${end}`,
    };
  });

  const columns = [
    {
      accessorKey: "order_id",
      header: "Order Id",
      size: 70,
      Cell: ({ row }) => (
        <Link to={`/order/view/${row.original.order_id}`}>
          {decodeHtml(row.original.order_id)}
        </Link>
      ),
    },
    {
      accessorKey: "name",
      header: "Name",
      size: 70,
    },
    {
      accessorKey: "client",
      header: "Clients",
      size: 70,
       Cell: ({ row }) => (
        <Link
          to={`/client/view/${row.original.client_id}`}
        >
          {decodeHtml(row.original.client)}
        </Link>
      ),
    },
    {
      accessorKey: "role_name",
      header: "Roles",
      size: 70,
    },
    {
      accessorKey: "role_manager",
      header: "Role Manager",
      size: 70,
    },
    {
      accessorKey: "assign_to",
      header: "Assigned To",
      size: 70,
    },
    {
      accessorKey: "business_developed",
      header: "BD By",
      size: 70,
    },
    {
      accessorKey: "invoice_number",
      header: "Invoice Number",
      size: 40,
    },
    {
      accessorKey: "created_date",
      header: "Created",
      size: 40,
    },
    {
      accessorKey: "raised_date",
      header: "Raised",
      size: 40,
    },
    {
      accessorKey: "issue_date",
      header: "Issued",
      size: 40,
    },
    {
      accessorKey: "collection_date",
      header: "collection",
      size: 40,
    },
    {
      accessorKey: "entity",
      header: "Entity",
      size: 70,
    },
    {
      accessorKey: "amount",
      header: "Amount",
      size: 70,
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 70,
    },
    {
      accessorKey: "retainer_amount_inr",
      header: "Total(INR)",
      size: 70,
      Cell: ({ row }) =>
        currency_converter_inr(row.original.retainer_amount_inr),
    },
  ];
  const columns_prev = [
    {
      accessorKey: "order_id",
      header: "Order Id",
      size: 70,
      Cell: ({ row }) => (
        <Link to={`/order/view/${row.original.order_id}`}>
          {decodeHtml(row.original.order_id)}
        </Link>
      ),
    },
    {
      accessorKey: "name",
      header: "Name",
      size: 70,
    },
    {
      accessorKey: "client",
      header: "Clients",
      size: 70,
    },
    {
      accessorKey: "role_name",
      header: "Roles",
      size: 70,
    },
    {
      accessorKey: "role_manager",
      header: "Role Manager",
      size: 70,
    },
    {
      accessorKey: "assign_to",
      header: "Assigned To",
      size: 70,
    },
    {
      accessorKey: "business_developed",
      header: "BD By",
      size: 70,
    },
    {
      accessorKey: "invoice_number",
      header: "Invoice Number",
      size: 40,
    },
    {
      accessorKey: "created_date",
      header: "Created",
      size: 40,
    },
    {
      accessorKey: "raised_date",
      header: "Raised",
      size: 40,
    },
    {
      accessorKey: "issue_date",
      header: "Issueed",
      size: 40,
    },
    {
      accessorKey: "collection_date",
      header: "collection",
      size: 40,
    },
    {
      accessorKey: "entity",
      header: "Entity",
      size: 70,
    },
    {
      accessorKey: "amount",
      header: "Amount",
      size: 70,
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 70,
    },
    {
      accessorKey: "retainer_amount_inr",
      header: "Total(INR)",
      size: 70,
      Cell: ({ row }) =>
        currency_converter_inr(row.original.retainer_amount_inr),
    },
  ];
const canChangeUser = user?.designation_id === 1 || user?.admin === '1';

  return (
    <>
      <div className="content">
        <div className="d-flex align-items-center justify-content-between page-breadcrumb mb-3 flex-wrap">
  
          <div className="my-auto">
            <h2 className="mb-0">Business Delivered</h2>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
              <button className="btn btn-light btn-sm" onClick={handleBack}>
                <i className="fa-solid fa-arrow-left me-1"></i> Back to Orders
              </button>
            <div style={{ minWidth: "140px" }}>
              <select
                className="form-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {financialYears.map((fy) => (
                  <option key={fy.value} value={fy.value}>
                    {fy.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="dropdown">
              <button
                className="btn btn-light dropdown-toggle"
                type="button"
                data-bs-toggle={canChangeUser ? "dropdown" : ""}
                disabled={!canChangeUser}
              >
                {selectedUser
                  ? `${selectedUser.firstname} ${selectedUser.lastname}`
                  : "Select User"}
              </button>

              <ul className="dropdown-menu">

                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setSelectedUser({
                        id: "9",
                        firstname: "Firm",
                        lastname: "Wide",
                      });

                      setValue("user_id", "9");
                    }}
                  >
                    Firm Wide
                  </button>
                </li>

                {userList?.user?.map((item) => (
                  <li key={item.id}>
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setSelectedUser(item);
                        setValue("user_id", item.id);
                      }}
                    >
                      {item.firstname} {item.lastname}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
        <div className="border-0">
          <div className="row flex-fill">
            <div className="col-xxl-12 d-flex">
              <div className="row flex-fill">
                <div className="col-md-3 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <h3
                        className="d-flex fs-17 mb-2"
                        style={{ flexDirection: "row" }}
                      >
                        <span className="avatar rounded-circle bg-purple mb-1 me-2">
                          <i className="fa-solid fa-money-bills"></i>
                        </span>
                        <div
                          className="d-flex"
                          style={{
                            flexDirection: "column",
                            justifyContent: "space-evenly",
                          }}
                        >
                          <span className="fw-lighter">
                            Total Delivered (FY)
                          </span>
                          <span>
                            {currency_converter_inr(
                              data?.revenue_current_year?.[0]?.total_revenue,
                            )}
                          </span>
                        </div>
                      </h3>
                      <div className="d-flex align-items-center">
                        <span className="fw-10">
                          Last Year:{" "}
                          {currency_converter_inr(
                            data?.revenue_prev_year?.[0]?.sum,
                          )}
                        </span>
                      </div>
                      <div>
                        <p className="fs-14 fw-medium mb-0 text-gray-5 mb-1">
                          Target: {" "}
                          <b>{formatRevenueInCr(data?.revenueTarget || 0)}</b>
                          {" ("}{Number(data?.revenueAchievement || 0).toFixed(1)}{"% Achieved)"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 d-flex">
                  <div className="card flex-fill div-focus-border">
                    <div className="card-body">
                      <h3
                        className="d-flex fs-17 mb-3"
                        style={{ flexDirection: "row" }}
                      >
                        <span className="avatar rounded-circle bg-pink mb-1 me-2">
                          <i className="fa-solid fa-bullseye"></i>
                        </span>
                        <div
                          className="d-flex"
                          style={{
                            flexDirection: "column",
                            justifyContent: "space-evenly",
                          }}
                        >
                          <span className="fw-lighter">Total Closed</span>
                          <span>
                            {data?.revenue_current_year?.[0]?.closer_count}
                          </span>
                        </div>
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <h3
                        className="d-flex fs-17 mb-3"
                        style={{ flexDirection: "row" }}
                      >
                        <span className="avatar rounded-circle bg-warning mb-1 me-2">
                          <i className="fa-solid fa-chart-simple"></i>
                        </span>
                        <div
                          className="d-flex"
                          style={{
                            flexDirection: "column",
                            justifyContent: "space-evenly",
                          }}
                        >
                          <span className="fw-lighter">YOY Growth</span>
                          <span> {data?.revenue_growth}%</span>
                        </div>
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <h3
                        className="d-flex fs-17 mb-3"
                        style={{ flexDirection: "row" }}
                      >
                        <span className="avatar rounded-circle bg-secondary mb-1 me-2">
                          <i className="fa-solid fa-universal-access"></i>
                        </span>
                        <div
                          className="d-flex"
                          style={{
                            flexDirection: "column",
                            justifyContent: "space-evenly",
                          }}
                        >
                          <span className="fw-lighter">Referred Business</span>
                          <span>
                            {currency_converter_inr(
                              data?.revenue_current_year?.[0]
                                ?.referred_business_revenue,
                            )}
                          </span>
                        </div>
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {showMonthlyTargetModal && (
            <AddMonthlyRevenueTarget
              employeeId={selectedUser?.id}
              employeeName={`${selectedUser?.firstname || ""} ${selectedUser?.lastname || ""}`}
              onClose={() => setShowMonthlyTargetModal(false)}
              onSuccess={() => {
                setShowMonthlyTargetModal(false);
                get_revenue_data();
              }}
            />
          )}
          {showMonthlyNetaddTargetModal && (
            <AddMonthlyNetaddTarget
              employeeId={selectedUser?.id}
              employeeName={`${selectedUser?.firstname || ""} ${selectedUser?.lastname || ""}`}
              onClose={() => setShowMonthlyNetaddTargetModal(false)}
              onSuccess={() => {
                setShowMonthlyNetaddTargetModal(false);
                get_revenue_data();
              }}
            />
          )}
          <div className="row">
            <div className="col-xxl-6 col-xl-6" style={{ minWidth: 0 }}>
              <div className="card flex-fill">
                <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                  <h5
                    className="mb-2 d-flex"
                    style={{ flexDirection: "column" }}
                  >
                    Revenue Trend
                  </h5>
                   {canAddMonthlyTarget && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm mb-2"
                        onClick={() => setShowMonthlyTargetModal(true)}
                      >
                        <i className="ti ti-plus me-1"></i>
                        Add Monthly Target
                      </button>
                    )}
                </div>
                <div className="card-body">
                  <RevenueTrend 
                    chart_data={data?.revenue_comparison} 
                    yearly_target_data={data?.yearly_revenue_target}
                    monthly_target_data={data?.monthly_revenue_target}
                    selectedFY={selectedYear}
                  />
                </div>
              </div>
            </div>
            <div className="col-xxl-6 col-xl-6">
              <div className="card flex-fill">
                <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                  <h5
                    className="mb-2 d-flex"
                    style={{ flexDirection: "column" }}
                  >
                    Contribution Breakdown
                  </h5>
                </div>
                <div className="card-body">
                  <MonthlyContribution chart_data={data?.revenue_monthly} />
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xxl-6 col-xl-6" style={{ minWidth: 0 }}>
              <div className="card flex-fill">
                <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                  <h5
                    className="mb-2 d-flex"
                    style={{ flexDirection: "column" }}
                  >
                    Net-Add Trend
                  </h5>
                  {canAddMonthlyTarget && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm mb-2"
                        onClick={() => setShowMonthlyNetaddTargetModal(true)}
                      >
                        <i className="ti ti-plus me-1"></i>
                        Add Net-Add Monthly Target
                      </button>
                    )}
                </div>
                <div className="card-body">
                  <NetAddTrend 
                    chart_data={data?.netadd_comparison}
                    yearly_target_data={data?.yearly_netadd_target}
                    monthly_target_data={data?.monthly_netadd_target}
                    selectedFY={selectedYear} 
                  />
                </div>
              </div>
            </div>
            <div className="col-xxl-6 col-xl-6">
              <div className="card flex-fill">
                <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                  <h5
                    className="mb-2 d-flex"
                    style={{ flexDirection: "column" }}
                  >
                    Net-Add Contribution Breakdown
                  </h5>
                </div>
                <div className="card-body">
                  <MonthlyContribution chart_data={data?.netadd_monthly} />
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xxl-6 col-xl-6">
              <div className="card flex-fill">
                <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                  <h5
                    className="mb-2 d-flex"
                    style={{ flexDirection: "column" }}
                  >
                    By Geography
                    <span className="fs-12 fw-light mt-1">
                      Business delivered by region
                    </span>
                  </h5>
                </div>
                <div className="card-body">
                  <ByGeography chart_data={data?.revenue_geography} />
                </div>
              </div>
            </div>
            <div className="col-xxl-6 col-xl-6">
              <div className="card flex-fill">
                <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                  <h5
                    className="mb-2 d-flex"
                    style={{ flexDirection: "column" }}
                  >
                    By Industry
                    <span className="fs-12 fw-light mt-1">
                      Business delivered by sector
                    </span>
                  </h5>
                </div>
                <div className="card-body">
                  <ByIndustry chart_data={data?.revenue_industry} />
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xxl-6 col-xl-6">
              <div className="card flex-fill">
                <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                  <h5
                    className="mb-2 d-flex"
                    style={{ flexDirection: "column" }}
                  >
                    Team Performance
                    <span className="fs-12 fw-light mt-1">
                      Performance breakdown for your team
                    </span>
                  </h5>
                </div>
                <div className="card-body">
                  <TeamRevenueAnalysis chart_data={data?.revenue_team_member} />
                </div>
              </div>
            </div>
            <div className="col-xxl-6 col-xl-6">
              <div className="card flex-fill">
                <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                  <h5
                    className="mb-2 d-flex"
                    style={{ flexDirection: "column" }}
                  >
                    VOBT Analysis
                    <span className="fs-12 fw-light mt-1">
                      Last 5 years performance metrics
                    </span>
                  </h5>
                </div>
                <div className="card-body">
                  <VOBTAnalysis
                    chart_data={
                      user.admin === "0"
                        ? data?.revenue_vobt_5years
                        : data?.revenue_vobt_firm_5years
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="card">
              <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                <h5 className="mb-2 d-flex" style={{ flexDirection: "column" }}>
                  Current Year Invoices
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="custom-datatable-filter table-responsive">
                  <div className="row p-4 mt-3">
                    {options.length > 0 && (
                      <MaterialReactTable
                        columns={columns}
                        data={options}
                        enableGrouping
                        initialState={{
                          density: "compact",
                          grouping: ["client"], // group by company_name
                          expanded: {}, // expand groups by default
                          pagination: { pageSize: 100 },
                          columnOrder: [
                            "order_id",
                            "name",
                            "client",
                            "role_name",
                            "role_manager",
                            "assign_to",
                            "business_developed",
                            "invoice_number",
                            "created_date",
                            "raised_date",
                            "issue_date",
                            "collection_date",
                            "entity",
                            "status",
                            "amount",
                            "retainer_amount_inr",
                          ],
                          columnPinning: {
                            left: [], // 👈 no pinned columns
                            right: [],
                          },
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="card">
              <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                <h5 className="mb-2 d-flex" style={{ flexDirection: "column" }}>
                  Previous Year Invoices
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="custom-datatable-filter table-responsive">
                  <div className="row p-4 mt-3">
                    {previousYearOptions.length > 0 && (
                      <MaterialReactTable
                        columns={columns_prev}
                        data={previousYearOptions}
                        enableGrouping
                        initialState={{
                          density: "compact",
                          grouping: ["client"], // group by company_name
                          expanded: {}, // expand groups by default
                          pagination: { pageSize: 100 },
                          columnOrder: [
                            "order_id",
                            "name",
                            "client",
                            "role_name",
                            "role_manager",
                            "assign_to",
                            "business_developed",
                            "invoice_number",
                            "created_date",
                            "raised_date",
                            "issue_date",
                            "collection_date",
                            "entity",
                            "status",
                            "amount",
                            "retainer_amount_inr",
                          ],
                          columnPinning: {
                            left: [], // 👈 no pinned columns
                            right: [],
                          },
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default RevenueDetails;
